import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { shopifyHelpers } from "../utils/shopify";
import { TOP16_HANDLES, TOP16_TITLE_MATCHES } from "../utils/top16";
import { CATEGORY_TAGS, GENERATION_TAGS } from "../utils/tagMappings";
import ProductCard from "../components/ProductCard";
import Layout from "../components/Layout";
import BackgroundWrapper from "../components/BackgroundWrapper";
import bgtrace from "../assets/bgtrace1.jpeg";

import * as Select from "@radix-ui/react-select";
import { ChevronDown, ChevronUp, Check } from "lucide-react";

const ShopPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const productHandlesParam = params.get("products");
  const selectedHandles = productHandlesParam ? productHandlesParam.split(",") : [];

  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [generation, setGeneration] = useState("All Gens");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ✅ Detect query param changes and scroll to top
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");

    if (categoryParam) {
      if (categoryParam === "top-sellers") {
        setCategory("Top Sellers");
      } else {
        // Map URL parameters to category names
        const categoryMapping = {
          "lighting": "Lighting",
          "suspension": "Suspension", 
          "armor": "Armor",
          "accessories": "Accessories",
          "mounts": "Mounts",
          "apparel": "Apparel",
          "tgmade": "TGMade"
        };
        
        const matchedCategory = categoryMapping[categoryParam];
        if (matchedCategory) {
          setCategory(matchedCategory);
        }
      }
      
      // Scroll to top when category changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setCategory("All Categories");
    }
  }, [location.search]);

  // ✅ Track window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Responsive product limit
  const PRODUCTS_PER_PAGE =
    windowWidth < 768 ? 6 : windowWidth < 1024 ? 9 : 16;

  useEffect(() => {
    setCurrentPage(1);
  }, [search, category, generation, location.search]);

  useEffect(() => {
    async function loadProducts() {
      try {
        const products = await shopifyHelpers.getAllProducts(250);
        setAllProducts(products);
      } catch (err) {
        console.error("❌ Error loading products:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, []);

  // ✅ Filtering logic (unchanged)
  const filteredProducts = allProducts.filter((p) => {
    if (category === "Top Sellers") {
      return (
        TOP16_HANDLES.includes(p.handle) ||
        TOP16_TITLE_MATCHES.some((title) =>
          p.title.toLowerCase().includes(title.toLowerCase())
        )
      );
    }

    if (selectedHandles.length > 0) return selectedHandles.includes(p.handle);

    const matchesSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      category === "All Categories" ||
      (CATEGORY_TAGS[category] &&
        p.tags.some(
          (tag) => tag.toLowerCase() === CATEGORY_TAGS[category].toLowerCase()
        ));
    const matchesGeneration =
      generation === "All Gens" ||
      (GENERATION_TAGS[generation] &&
        p.tags.some(
          (tag) =>
            tag.toLowerCase() === GENERATION_TAGS[generation].toLowerCase()
        ));
    return matchesSearch && matchesCategory && matchesGeneration;
  });

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  const getPageNumbers = () => {
    let pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages = [1, 2, 3, "...", totalPages];
      } else if (currentPage >= totalPages - 2) {
        pages = [1, "...", totalPages - 2, totalPages - 1, totalPages];
      } else {
        pages = [
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        ];
      }
    }
    return pages;
  };

  if (loading) return <div className="p-10 text-black">Loading products...</div>;

  return (
    <Layout>
      <BackgroundWrapper
        desktopImage={bgtrace}
        desktopSize="120%"
        desktopPosition="center"
        desktopAttachment="fixed"
        mobilePosition="center"
        treatTabletAsMobile={true}
      >
        <div className="relative max-w-7xl mx-auto p-4 sm:p-6 min-h-screen">
          <h1 className="text-5xl font-extrabold mb-8 pt-10 tracking-tight text-center">
            <span className="text-black">Shop</span>{" "}
            <span className="text-brandred">All Products</span>
          </h1>

          {/* 🔍 Filters */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-8 relative z-10">
            <input
              type="text"
              placeholder="Search for parts, accessories, or installations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-400 rounded-lg px-3 py-2 w-full sm:w-1/2 md:w-1/3 
                         bg-gray-800 text-white placeholder-gray-200 
                         focus:ring-2 focus:ring-brandred focus:bg-gray-900 
                         hover:bg-gray-850 hover:border-gray-300 transition-all duration-200"
            />

            {/* Category */}
            <Select.Root value={category} onValueChange={setCategory}>
              <Select.Trigger
                className="inline-flex items-center justify-between border border-gray-400 rounded-lg px-3 py-2 
                           w-full sm:w-40 bg-gray-800 text-white 
                           focus:ring-2 focus:ring-brandred focus:bg-gray-900 
                           hover:bg-gray-850 hover:border-gray-300 transition-all duration-200"
              >
                <Select.Value />
                <Select.Icon>
                  <ChevronDown className="h-4 w-4" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="z-[9999] bg-gray-800 text-white rounded-lg shadow-xl border border-gray-600"
                  position="popper"
                >
                  <Select.ScrollUpButton className="flex items-center justify-center h-6">
                    <ChevronUp className="h-4 w-4" />
                  </Select.ScrollUpButton>
                  <Select.Viewport className="p-1">
                    <Select.Item value="All Categories" className="px-3 py-2 hover:bg-gray-700 rounded cursor-pointer">
                      <Select.ItemText>All Categories</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="h-3 w-3" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="Top Sellers" className="px-3 py-2 hover:bg-gray-700 rounded cursor-pointer">
                      <Select.ItemText>Top Sellers</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="h-3 w-3" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    {Object.keys(CATEGORY_TAGS).map((cat) => (
                      <Select.Item key={cat} value={cat} className="px-3 py-2 hover:bg-gray-700 rounded cursor-pointer">
                        <Select.ItemText>{cat}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Check className="h-3 w-3" />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>

            {/* Generation */}
            <Select.Root value={generation} onValueChange={setGeneration}>
              <Select.Trigger
                className="inline-flex items-center justify-between border border-gray-400 rounded-lg px-3 py-2 
                           w-full sm:w-40 bg-gray-800 text-white 
                           focus:ring-2 focus:ring-brandred focus:bg-gray-900 
                           hover:bg-gray-850 hover:border-gray-300 transition-all duration-200"
              >
                <Select.Value />
                <Select.Icon>
                  <ChevronDown className="h-4 w-4" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="z-[9999] bg-gray-800 text-white rounded-lg shadow-xl border border-gray-600"
                  position="popper"
                >
                  <Select.ScrollUpButton className="flex items-center justify-center h-6">
                    <ChevronUp className="h-4 w-4" />
                  </Select.ScrollUpButton>
                  <Select.Viewport className="p-1">
                    <Select.Item value="All Gens" className="px-3 py-2 hover:bg-gray-700 rounded cursor-pointer">
                      <Select.ItemText>All Gens</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="h-3 w-3" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    {Object.keys(GENERATION_TAGS).map((gen) => (
                      <Select.Item key={gen} value={gen} className="px-3 py-2 hover:bg-gray-700 rounded cursor-pointer">
                        <Select.ItemText>{gen}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Check className="h-3 w-3" />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>

          {/* 📦 Product Grid */}
          <section>
            <div className="bg-gray-200 rounded-xl p-6 shadow-md">
              <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredProducts.length > 0 ? (
                  filteredProducts
                    .slice(
                      (currentPage - 1) * PRODUCTS_PER_PAGE,
                      currentPage * PRODUCTS_PER_PAGE
                    )
                    .map((product) => (
                      <div
                        key={product.id}
                        className="bg-gray-600 rounded-lg p-4 shadow-md hover:shadow-lg hover:bg-gray-700 transition"
                      >
                        <ProductCard product={product} />
                      </div>
                    ))
                ) : (
                  <p className="text-center text-gray-500 col-span-full">
                    🚫 No products match your filters.
                  </p>
                )}
              </div>
            </div>

            {/* 🔢 Pagination (unchanged) */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-8">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors duration-200 ${
                    currentPage === 1
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-brandred text-white hover:bg-red-700"
                  }`}
                >
                  Prev
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={idx} className="px-3 py-2 text-gray-500">
                      ...
                    </span>
                  ) : (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors duration-200 ${
                        currentPage === page
                          ? "bg-red-700 text-white"
                          : "bg-brandred text-white hover:bg-red-700"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors duration-200 ${
                    currentPage === totalPages
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-brandred text-white hover:bg-red-700"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>
      </BackgroundWrapper>
    </Layout>
  );
};

export default ShopPage;
